import React from "react";
import { connect } from "react-redux";

import { fetchInventories } from "../../../utils/queries/production/inventoryQuery";
import { getStorage } from "../../../utils/storage";
import { hasNationalInventory } from "../../../utils/handleAccessRoles";
import "./styles.css";
import { Table } from "../../Table";
import language from "../../../language";

class ViewProducts extends React.Component {
  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    error: {},
    user: {},
  };

  componentDidMount = async () => {
    const user = await getStorage();
    await this.setState({ user });

    this.getData(true, {});
  };

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].donation_number,
        key: "donation.donationNumber",
      },
      {
        title: language[this.props.defaultLanguage].product_type,
        key: "component.productType.name",
      },
      {
        title: language[this.props.defaultLanguage].code,
        key: "code",
      },
      {
        title: language[this.props.defaultLanguage].product,
        key: "name",
      },
      {
        title: language[this.props.defaultLanguage].blood_group,
        key: "bloodGroup",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].volume,
        key: "volume",
      },
      {
        title: language[this.props.defaultLanguage].concentration,
        key: "concentrationValue",
      },
      {
        title: language[this.props.defaultLanguage].phlebotomy_date,
        key: "donation.collectionDate",
        formatTime: "ll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].expired_date,
        key: "expiredDate",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].labelled_by,
        key: "verifiedBy.pin",
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].stock,
        key: "stock.name",
      },

      {
        title: language[this.props.defaultLanguage].source,
        render: (item) => item?.fromService?.name || item?.sourceStock?.name || "-",
      },

      {
        title: language[this.props.defaultLanguage].reason,
        key: "reason",
      },
      {
        title: language[this.props.defaultLanguage].shipment_status,
        key: "shipmentInfo.status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].transferred_date,
        key: "shipmentInfo.date",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].transferred_by,
        key: "shipmentInfo.user.pin",
      },
      {
        title: language[this.props.defaultLanguage].reception,
        key: "receptionInfo.status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].reception_date,
        key: "receptionInfo.date",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].received_by,
        key: "receptionInfo.user.pin",
      },
      {
        title: language[this.props.defaultLanguage].inventory_status,
        key: "inventoryStatus",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    return headers;
  }

  getData = async (isLoading, search = {}) => {
    try {
      const { defaultLanguage } = this.props;
      const { page, limit } = this.state;
      const user = await getStorage();

      this.setState({ isLoading });

      // Must match the figure on the card exactly, or the drill-down
      // contradicts the number that was clicked. The card counted one B-
      // unit while this list showed a page of them, because it filtered on
      // none of the three conditions the count applies.
      //
      // exactStock, not stock: fetchInventories does not forward `stock`, so
      // the OR fallback took over and widened the list to units this entity
      // had already sent away.
      //
      // The shipmentInfo branches that stood here set countStatusParams twice,
      // so the first assignment was dead, and neither matched the card. The
      // card does not look at shipment status at all.
      //
      // A caller that is showing one card per facility passes that facility
      // in; a caller showing the country summed into one card passes nothing
      // and falls back to the account own stock, which is absent for an
      // account that is not pinned to a blood bank. Either way the list is
      // scoped exactly like the count on the card.
      // An account holding the national inventory role is looking at every
      // blood bank, so its own must not creep back in - neither here nor
      // through the stock-or-sourceStock fallback inside fetchInventories,
      // which is what noStockFilter turns off.
      const countryWide = await hasNationalInventory();
      const facility =
        this.props.stock || (countryWide ? undefined : user.stock);

      let params = {
        page,
        ...search,
        exactStock: facility,
        noStockFilter: facility ? undefined : true,
        bloodGroup: this.props.bloodGroup,
        productType: this.props.productIds,
        limit: this.props.limit || limit,
        status: "labelled",
        inventoryStatus: "INSTOCK",
        excludeIncomingReception: true,
        onlyNotExpired: true,
      };

      const { data, count, totalRecords } = await fetchInventories(defaultLanguage, params);

      this.setState({
        data,
        totalPageCount: count,
        totalRecords,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));

    await this.getData(true, {
      q: search || search !== "" ? search : undefined,
    });

    this.setState({
      searching: false,
    });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true, { page });
      }
    );
  }

  render() {
    return (
      <>
        <Table
          isLoading={this.state.isLoading}
          search_text={this.state.search_text}
          addSearchButton
          searching={this.state.searching}
          handleSearch={this.handleSearch.bind(this)}
          handlePressSearch={() =>
            this.handleRemoteSearch(this.state.search_text)
          }
          isSearch
          data={this.state.data}
          headers={this.returnTableHeaders()}
          totalPageCount={this.state.totalPageCount}
          totalRecords={this.state.totalRecords}
          handlePagination={this.handlePagination.bind(this)}
          page={this.state.page}
          limit={this.state.limit}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(ViewProducts);
